import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ACTIVITY_FEED_TILE, Color, FIGURE_BLOCK_HEIGHT_RATIO, MY_TODOS_TILE, PENDING_APPROVALS_TILE, PROJECT_PERFORMANCE_TILE, SALES_REPORTING_TILE,
  ON_CALL_ALERT_ENTRY_TYPE, Size, TILE_GAP_PX, WORK_QUEUE_TILE, dashboardTileSpan, documentName, onCallNoticePath, t,
  type ActivityFeedEntryRow, type PendingApprovalsSummary, type ProjectPerformanceAnswer, type ResolvedQueue, type SalesDashboardAnswer, type TilePlacement,
  DEFAULT_DATE_FORMAT, formatDateTime, type DateFormat, formatPercent
} from '@bms2/shared';
import { Tile, type TileItem } from '../components/composites/Tile';
import { TileCanvas, type CanvasTile } from '../components/composites/TileCanvas';
import { Button } from '../components/controls/Button';
import { Checkbox } from '../components/controls/Checkbox';
import { Modal } from '../components/modals/Modal';
import { PageModule } from './PageModule';
import { useDateFormat } from './ViewerZone';
import type { TodosAnswer } from './AppShell';
import { PROJECT_PERFORMANCE_PATH, PROJECT_PERFORMANCE_ROUTE, SALES_DASHBOARD_PATH, SALES_DASHBOARD_ROUTE, money } from '../pages/reports/api';
import { useApi } from '../pages/records/api';
import styles from './shell.module.css';

/** The To-Do page's route, the role queue's own (piece 12's registry; decision 0344). */
export const TODOS_ROUTE = '/todos/role';

/** The Pending Approvals queue's route, which the tile's own footer link opens (decision 0599). */
export const APPROVALS_QUEUE_ROUTE = '/approvals';

/**
 * The Dashboard (piece 12): the role-based landing page at `/`, built on Tile
 * Canvas rather than a bespoke layout. 🔨 A tile with no `DashboardTile` row
 * does not appear — the resolver decides what reaches this component, and the
 * component only knows how to draw the three rows the piece seeds. A dashboard
 * defaults every Tile to unlocked (piece 4), and a person's rearrangement
 * writes their own `SavedView` row through `onArrange`. 🔨 The library adds
 * and removes tiles from the rows the viewer passes and never from anywhere
 * else: a removed tile is the person's own `hidden` list on the same row,
 * and the seeded order still governs what they never rearranged (decision
 * 0264).
 * 💰 The dashboard was a plain CSS grid, not the canvas, so the queue tile ran
 * the page's whole height and nothing could be rearranged (found while
 * building, 2026-09-09).
 */
export interface DashboardProps {
  /** The tiles the viewer passes, already in their own arrangement; the library lists every one of them. */
  tiles: readonly { key: string; label: string }[];
  /** The tiles the viewer removed from their own dashboard (decision 0264). */
  hiddenTiles?: readonly string[];
  queues: readonly ResolvedQueue[];
  feed: readonly ActivityFeedEntryRow[];
  /** The viewer's display name, for the banner's greeting. */
  personName?: string;
  /** The viewer's zone, which the feed's times render in. */
  timeZone?: string;
  /** The My to-dos tile's count and top rows (decision 0344); absent for a viewer who does not pass that tile. */
  todos?: TodosAnswer | null;
  /** The Pending Approvals tile's numbers (decision 0599); absent for a viewer who does not pass that tile. */
  approvals?: PendingApprovalsSummary | null;
  /** The two report tiles read their own pages' answers from here (decision 0600); the harness passes none. */
  apiBaseUrl?: string;
  onArrange?: (order: readonly string[], hidden: readonly string[]) => void;
  /** 🔒 A card that stands above the tiles, the passkey offer among them (decision 1442; T693). */
  topCard?: ReactNode;
  /** False until the frame's first read answers; neither empty sentence is drawn before then (T725 review). The harness passes a read list and leaves it true. */
  loaded?: boolean;
}

/** The dashboard's tiles declare their own bounds; the canvas assigns width and never reaches inside. */
export const DASHBOARD_TILE_MIN_WIDTH = 280;
export const DASHBOARD_TILE_MAX_WIDTH = 1200;

/** 🔨 The gutter is the `--tile-gap` token's own number (decision 0601), never a pixel written here. */
export const DASHBOARD_CANVAS_OPTIONS = { columns: 3, twoColumnBelow: 900, oneColumnBelow: 560, gap: TILE_GAP_PX, blockHeightRatio: FIGURE_BLOCK_HEIGHT_RATIO };

/** The tile keys are the shared module's, so the seed, the resolver and this screen read one spelling (piece 12). */
export { ACTIVITY_FEED_TILE, MY_TODOS_TILE, PENDING_APPROVALS_TILE, PROJECT_PERFORMANCE_TILE, SALES_REPORTING_TILE, WORK_QUEUE_TILE };

/**
 * The My to-dos tile (decision 0344): the open count and the top rows the
 * shell's read carried, each linking to the To-Do page, and a last row that
 * says how many are open. A viewer whose read carried no rows sees the empty
 * note rather than nothing, since the tile is on their dashboard.
 */
function todoItems(todos: TodosAnswer | null | undefined): TileItem[] {
  if (!todos || todos.rows.length === 0) return [{ type: 'note', text: t('todos.tile.empty') }];
  return [
    ...todos.rows.map((row) => ({ type: 'linkRow' as const, label: row.title, route: TODOS_ROUTE, value: row.dueDate ?? t('todos.noDue'), key: row.id })),
    { type: 'linkRow' as const, label: t('todos.tile.seeAll'), route: TODOS_ROUTE, value: `${todos.count} ${t('todos.tile.open')}`, key: 'todos:all' },
  ];
}

/**
 * 💰 Zero is shown, not hidden: a queue that disappears when empty is a queue
 * nobody learns to look for. A queue whose page a later piece builds shows no
 * number at all rather than a false zero, which would read as empty.
 */
function queueItems(queues: readonly ResolvedQueue[]): TileItem[] {
  if (queues.length === 0) return [{ type: 'note', text: t('shell.dashboard.noQueues') }];
  // 🔨 A queue whose page a later piece builds shows no number and is not a link: a link to a page that does not exist reads as broken (found while building, 2026-09-09).
  return queues.map((queue) =>
    queue.count === null
      ? { type: 'fullWidthRow' as const, text: queue.label, value: t('shell.dashboard.notBuilt'), key: queue.key }
      : { type: 'linkRow' as const, label: queue.label, route: queue.route, value: queue.count, key: queue.key },
  );
}

function whenIn(instant: string | Date, zone: string, format: DateFormat = DEFAULT_DATE_FORMAT): string {
  try {
    return formatDateTime(instant, zone, format);
  } catch {
    // silent: an instant the zone cannot format is drawn as its own text, the page's fallback.
    return String(instant).slice(0, 16).replace('T', ' ');
  }
}

function feedItems(feed: readonly ActivityFeedEntryRow[], zone: string, dateFormat: DateFormat = DEFAULT_DATE_FORMAT): TileItem[] {
  if (feed.length === 0) return [{ type: 'note', text: t('shell.dashboard.feedEmpty') }];
  // 🔒 T2398: an on-call notice names its job and links to the job's Details view, where the viewer's own Acknowledge stands.
  const routeOf = (entry: ActivityFeedEntryRow): string | null => (entry.entryType === ON_CALL_ALERT_ENTRY_TYPE && entry.taggedRecordType === 'WorkOrder' && entry.taggedRecordId ? onCallNoticePath(entry.taggedRecordId) : null);
  return [{ type: 'timeline', entries: feed.map((entry) => ({ when: whenIn(entry.occurredAt, zone, dateFormat), text: entry.summary, route: routeOf(entry) })) }];
}

/**
 * 🔒 The Pending Approvals tile as the owner's artifact draws it (decision
 * 0599): the count large with the caption beneath it, one row per document type
 * with its own count in a warning pill, and a footer link into the queue. 🔨 A
 * type with nothing waiting draws no row, so the tile says what is waiting and
 * not what could.
 */
function approvalItems(approvals: PendingApprovalsSummary | null): TileItem[] {
  if (!approvals || approvals.count === 0) return [{ type: 'note', text: t('approvals.tile.empty') }];
  return [
    ...approvals.byType.map((row) => ({
      type: 'fullWidthRow' as const,
      text: documentName(row.documentType),
      pill: { text: String(row.count), group: 'warning' as const },
      route: APPROVALS_QUEUE_ROUTE,
    })),
    { type: 'linkRow' as const, label: t('approvals.tile.open'), route: APPROVALS_QUEUE_ROUTE, value: `${approvals.mine} ${t('approvals.tile.yours')}`, key: 'approvals:open' },
  ];
}

/**
 * 🔒 The Project Performance tile (decision 0600): the projects running, the
 * ones whose consumed and complete figures have diverged, and the worst few by
 * that divergence, each row naming both figures the alarm reads.
 */
function projectItems(answer: ProjectPerformanceAnswer | null): TileItem[] {
  if (!answer) return [{ type: 'note', text: t('reports.tile.loading') }];
  if (answer.rows.length === 0) return [{ type: 'note', text: t('reports.projectPerformance.empty') }];
  const figure = (value: number | null | undefined) => (value === null || value === undefined ? t('reports.tile.noFigure') : formatPercent(value));
  const alarming = answer.rows.filter((row) => row.alarm.raised);
  return [
    ...[...alarming, ...answer.rows.filter((row) => !row.alarm.raised)].slice(0, 4).map((row) => ({
      type: 'fullWidthRow' as const,
      text: row.dealName,
      value: `${figure(row.percentConsumed)} / ${figure(row.percentComplete)}`,
      route: PROJECT_PERFORMANCE_ROUTE,
      ...(row.alarm.raised ? { pill: { text: t('reports.tile.diverged'), group: 'error' as const } } : {}),
    })),
    { type: 'linkRow' as const, label: t('reports.tile.openProjects'), route: PROJECT_PERFORMANCE_ROUTE, value: `${alarming.length} ${t('reports.tile.divergedCount')}`, key: 'projects:open' },
  ];
}

/**
 * 🔒 The Sales Reporting tile (decision 0600): the weighted pipeline as the
 * figure, one row per step of the revenue flow with its money, and the link into
 * the dashboard. 🔨 The Preliminary label the page must draw rides along, because
 * the number is as preliminary here as it is there.
 */
function salesItems(answer: SalesDashboardAnswer | null): TileItem[] {
  if (!answer) return [{ type: 'note', text: t('reports.tile.loading') }];
  return [
    ...answer.flow.map((step) => ({ type: 'fullWidthRow' as const, text: step.label, value: money(step.amountCents), route: SALES_DASHBOARD_ROUTE })),
    { type: 'note' as const, text: answer.preliminaryLabel },
    { type: 'linkRow' as const, label: t('reports.salesDashboard.weighted'), route: SALES_DASHBOARD_ROUTE, value: money(answer.weightedPipelineCents), key: 'sales:open' },
  ];
}

/**
 * 🔒 The large number a tile leads with, and the caption under it (the owner's
 * artifact, 2026-09-20): the count is the tile's whole point, so it is drawn
 * once at the tile's own figure size rather than as another row of the list.
 */
function TileFigure({ count, caption, route }: { count: number; caption: string; route: string }) {
  return (
    <Link className={styles.dashboardFigure} to={route} data-dashboard-figure={count}>
      <span className={styles.dashboardFigureCount}>{count}</span>
      <span className={styles.dashboardFigureCaption}>{caption}</span>
    </Link>
  );
}

export function Dashboard({ topCard, tiles, hiddenTiles = [], queues, feed, personName, timeZone = 'UTC', todos = null, approvals = null, apiBaseUrl = '', onArrange, loaded = true }: DashboardProps) {
  const dateFormat = useDateFormat();
  const [editing, setEditing] = useState(false);
  const [library, setLibrary] = useState(false);
  /** The person's own removals, drawn from the same list the write goes out from. */
  const [hidden, setHidden] = useState<readonly string[]>(hiddenTiles);
  /** The seeded order, or the person's own once they have rearranged; a move is written out and drawn from the same list. */
  const [order, setOrder] = useState<string[] | null>(null);
  const ordered = useMemo(() => {
    if (!order) return tiles;
    const byKey = new Map(tiles.map((tile) => [tile.key, tile]));
    return [...order.map((key) => byKey.get(key)).filter((tile): tile is { key: string; label: string } => tile !== undefined), ...tiles.filter((tile) => !order.includes(tile.key))];
  }, [tiles, order]);

  const shown = ordered.filter((tile) => !hidden.includes(tile.key));
  /**
   * 🔨 A report tile reads its own page's answer, and only while it is on the
   * dashboard (decision 0600): the two reads are a walk over deals and work
   * orders each, and the frame's own read runs on every page in the application.
   * 💰 The approvals numbers ride the frame's read instead, because the queue
   * row's count in the same answer has to agree with them.
   */
  const showing = (key: string) => shown.some((tile) => tile.key === key);
  const projects = useApi<ProjectPerformanceAnswer>(showing(PROJECT_PERFORMANCE_TILE) ? PROJECT_PERFORMANCE_PATH : null, apiBaseUrl);
  const sales = useApi<SalesDashboardAnswer>(showing(SALES_REPORTING_TILE) ? SALES_DASHBOARD_PATH : null, apiBaseUrl);
  const projectAnswer = projects.state.kind === 'ready' ? projects.state.data : null;
  const salesAnswer = sales.state.kind === 'ready' ? sales.state.data : null;

  /** The items each seeded tile draws; a tile this screen does not know draws nothing rather than a placeholder. */
  const itemsFor = (key: string): TileItem[] => {
    if (key === WORK_QUEUE_TILE) return queueItems(queues);
    if (key === ACTIVITY_FEED_TILE) return feedItems(feed, timeZone, dateFormat);
    if (key === MY_TODOS_TILE) return todoItems(todos);
    if (key === PENDING_APPROVALS_TILE) return approvalItems(approvals);
    if (key === PROJECT_PERFORMANCE_TILE) return projectItems(projectAnswer);
    if (key === SALES_REPORTING_TILE) return salesItems(salesAnswer);
    return [];
  };

  /** The tiles that lead with a large number: the count, its caption, and where clicking it goes. */
  const figureFor = (key: string) => {
    if (key === PENDING_APPROVALS_TILE) return { count: approvals?.count ?? 0, caption: t('approvals.tile.caption'), route: APPROVALS_QUEUE_ROUTE };
    if (key === PROJECT_PERFORMANCE_TILE && projectAnswer) return { count: projectAnswer.rows.length, caption: t('reports.tile.projectsRunning'), route: PROJECT_PERFORMANCE_ROUTE };
    return null;
  };

  const toggle = (key: string, visible: boolean) => {
    const next = visible ? hidden.filter((k) => k !== key) : [...hidden, key];
    setHidden(next);
    onArrange?.(order ?? ordered.map((tile) => tile.key), next);
  };

  const canvas: CanvasTile[] = shown.map((tile) => {
    const figure = figureFor(tile.key);
    return {
      // 🔨 The span is the seeded set's own, so the six tiles fill the rows they stand on (decision 0601).
      placement: { id: tile.key, span: dashboardTileSpan(tile.key), height: 1, locked: false } as TilePlacement,
      bounds: { minWidth: DASHBOARD_TILE_MIN_WIDTH, maxWidth: DASHBOARD_TILE_MAX_WIDTH },
      title: tile.label,
      render: () => (
        <div className={styles.dashboardSlot} data-dashboard-tile={tile.key}>
          <Tile title={tile.label} minWidth={DASHBOARD_TILE_MIN_WIDTH} maxWidth={DASHBOARD_TILE_MAX_WIDTH} items={itemsFor(tile.key)}>
            {figure ? <TileFigure count={figure.count} caption={figure.caption} route={figure.route} /> : null}
          </Tile>
        </div>
      ),
    };
  });

  return (
    <PageModule title={t('shell.dashboard.title')} icon="dashboard" subtitle={personName ? `${t('shell.dashboard.welcome')} ${personName}` : null}>
      {/* 🔨 The dashboard stacks at the page step every other page uses (decision 0601). */}
      <div className={styles.dashboardPage} data-shell="dashboard">
        {topCard}
        {editing ? (
          <div className={styles.dashboardLibraryBar} data-dashboard-library-bar="true">
            <Button size={Size.Small} color={Color.Navy} text={[t('shell.dashboard.tiles')]} actions={[() => setLibrary(true)]} />
          </div>
        ) : null}
        {/* 🔒 T725: a role carrying no tiles says so, and a person who removed every tile keeps the sentence that tells them how to bring one back. 💰 Before the frame's read answers the list is empty for everybody, so neither sentence is drawn until it has. */}
        {loaded && tiles.length === 0 ? <p className={styles.dashboardEmpty} data-dashboard-no-tiles="true">{t('shell.dashboard.noTiles')}</p> : null}
        {loaded && tiles.length > 0 && shown.length === 0 ? <p className={styles.dashboardEmpty} data-dashboard-empty="true">{t('shell.dashboard.allHidden')}</p> : null}
        <Modal
          open={library}
          title={t('shell.dashboard.tilesTitle')}
          subtitle={t('shell.dashboard.tilesSubtitle')}
          onCancel={() => setLibrary(false)}
          dataModal="dashboard-tiles"
          actions={[
            // 🔒 Decision 2140: the tile library writes nothing, so it carries one Close (decision 3063).
            { kind: 'close', label: t('modal.close'), onAct: () => setLibrary(false) },
          ]}
        >
          <div className={styles.dashboardLibrary} data-dashboard-library="true">
            {tiles.map((tile) => (
              <Checkbox key={tile.key} label={tile.label} name={tile.key} checked={!hidden.includes(tile.key)} onChange={(checked) => toggle(tile.key, checked)} />
            ))}
          </div>
        </Modal>
        <TileCanvas
          tiles={canvas}
          options={DASHBOARD_CANVAS_OPTIONS}
          label={t('shell.dashboard.title')}
          editing={editing}
          onEditingChange={setEditing}
          onArrange={(placements) => {
            const next = placements.map((placement) => placement.id);
            setOrder(next);
            onArrange?.(next, hidden);
          }}
        />
      </div>
    </PageModule>
  );
}
